/**
 * Pure helpers for the Dev Console team panel: which workspace member IS which
 * plugin collaborator.
 *
 * The two lists come from different endpoints and share no id:
 *   - `GET /vault/holders/:id/members` rows carry `principalId` + `username`
 *   - `GET /admin/plugins/:id/maintainers` rows carry `userId` + `username`
 * Both usernames are resolved server-side from the same user table, so the
 * handle is the join key. Lookups in kv-operations lowercase the handle, which
 * means the STORED case can vary — so the match is case-insensitive, and a
 * null on either side never matches (a deleted or unresolved account must not
 * collapse onto another null).
 *
 * Every active roster row lands in exactly one bucket: matched → the member
 * matrix; unmatched → "other collaborators"; the Fias owner row → aside. The
 * unit test pins that no userId appears twice.
 */

import type { PluginMaintainer, VaultHolderMemberDto } from '@/lib/api';

export function normalizeUsername(username: string | null | undefined): string | null {
  if (typeof username !== 'string') return null;
  const trimmed = username.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

export interface MatrixRow {
  member: VaultHolderMemberDto;
  /** The member's active collaborator row on the plugin, or null = no access. */
  roster: PluginMaintainer | null;
}

export interface TeamPartition {
  /** One row per ACTIVE workspace member, in the members endpoint's order. */
  matrix: MatrixRow[];
  /** Active collaborators who are not (matchable) members of the workspace. */
  others: PluginMaintainer[];
  /** The Fias platform account's owner row, if present. Never editable. */
  platformOwner: PluginMaintainer | null;
}

export function partitionTeam(
  members: readonly VaultHolderMemberDto[],
  roster: readonly PluginMaintainer[],
): TeamPartition {
  const activeRoster = roster.filter((r) => r.status === 'active');
  const platformOwner = activeRoster.find((r) => r.isPlatformAccount) ?? null;

  const rosterByHandle = new Map<string, PluginMaintainer>();
  for (const row of activeRoster) {
    if (row.isPlatformAccount) continue;
    const handle = normalizeUsername(row.username);
    if (handle && !rosterByHandle.has(handle)) rosterByHandle.set(handle, row);
  }

  const claimed = new Set<string>();
  const matrix: MatrixRow[] = [];
  for (const member of members) {
    if (member.status !== 'active') continue;
    const handle = normalizeUsername(member.username);
    const match = handle ? (rosterByHandle.get(handle) ?? null) : null;
    if (match) claimed.add(match.userId);
    matrix.push({ member, roster: match });
  }

  const others = activeRoster.filter((r) => !r.isPlatformAccount && !claimed.has(r.userId));

  return { matrix, others, platformOwner };
}

/** A stable, human-safe label for a roster row whose handle could not be resolved. */
export function describeUnresolvedAccount(userId: string): string {
  return `Unresolved account · …${userId.slice(-4)}`;
}
